import { InteractionStatus } from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { IUser, UserRole, AuthProvider } from 'tf-pulse-shared';
import { logger } from '../utils/logger';

export const useAuth = () => {
  const account = useAccount();
  const { instance, inProgress } = useMsal();
  const isMsalLoading = inProgress !== InteractionStatus.None;
  const isAuthenticated = useIsAuthenticated();
  const testApiKey = import.meta.env.VITE_TEST_API_KEY;

  if (testApiKey) {
    const testAutomationUser: IUser = {
      id: 'test-automation-user',
      email: 'test.automation@local.dev',
      name: 'Test Automation User',
      role: UserRole.USER,
      provider: AuthProvider.LOCAL,
      externalId: 'test-automation-user',
      createdAt: new Date('1970-01-01T00:00:00.000Z'),
    };

    return {
      user: testAutomationUser,
      isAuthenticated: true,
      isLoading: false,
      account: undefined,
    };
  }

  // Try to get account from MSAL instance if useAccount() returns null
  const effectiveAccount = account || instance.getAllAccounts()?.[0];

  // Debug-only: state of the auth hook. Gated by the logger so this never
  // reaches the browser console (and never prints account name/username) in
  // production.
  logger.debug('🔍 useAuth called:', {
    isAuthenticated,
    hasAccount: !!account,
    hasEffectiveAccount: !!effectiveAccount,
    accountFromHook: !!account,
    accountFromInstance: !!instance.getAllAccounts()?.[0],
    inProgress,
    isMsalLoading,
    totalAccounts: instance.getAllAccounts().length,
  });

  // Convert MSAL account to our IUser interface
  const user: IUser | undefined = effectiveAccount
    ? {
        id: effectiveAccount.localAccountId,
        email: effectiveAccount.username || '',
        name: effectiveAccount.name || effectiveAccount.username || '',
        role: UserRole.USER, // Default role, will be determined by backend
        provider: AuthProvider.MICROSOFT,
        externalId: effectiveAccount.localAccountId,
        createdAt: new Date(), // This will be set by backend
      }
    : undefined;

  // Log user creation without the full user object (id/email) to avoid
  // leaking PII into the console; gated to non-production via the logger.
  if (user) {
    logger.debug('🔍 useAuth - User object created:', { hasUser: true });
  } else {
    logger.debug('🔍 useAuth - No user object created (no effective account available)');
  }

  return {
    user,
    isAuthenticated,
    isLoading: isMsalLoading,
    account: effectiveAccount,
  };
};
